import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaServer,
  FaGlobe,
  FaShieldAlt,
  FaRocket,
  FaHeadset,
  FaCheck,
  FaCloud,
  FaDatabase,
  FaCog,
  FaChartLine,
  FaEnvelope,
  FaLock,
  FaExpand,
  FaTachometerAlt,
  FaDownload,
  FaUpload,
  FaStar,
  FaUsers,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaArrowRight,
} from "react-icons/fa";
import "./WebhostingPage.css";

const WebhostingPage = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Dynamic calculation of years since company founding
  const yearsInBusiness = new Date().getFullYear() - 1989;
  
  const [selectedPlan, setSelectedPlan] = useState("standard");
  const [billingCycle, setBillingCycle] = useState("yearly");

  const hostingPlans = [
    {
      id: "minimum",
      name: "MINIMUM",
      price: { yearly: 49 },
      popular: false,
      features: [
        "Max 1 doména",
        "Priestor max 4GB pre dáta/databázy/maily",
        "Max 10 PHP Workerov",
        "Memory limit a PHP limit: štandard",
        "Dáta na NVMe diskoch",
        "Zdarma SSL certifikát LETSENCRYPT",
        "Cloudflare ochrana proti útokom",
        "Správa Mail adries s DKIM a SPF",
        "Antispam ochrana mailov",
        "Neobmedzený počet emailových schránok",
        "Záloha DB/WEB/MAIL 1x denne po dobu 7 dní",
        "Podpora zákazníka telefonicky, štandardná odozva",
      ],
      ideal: "Malé webstránky a začínajúce projekty",
    },
    {
      id: "standard",
      name: "STANDARD",
      price: { yearly: 79 },
      popular: true,
      features: [
        "Max 3 domény",
        "Priestor do 25GB (do 50GB za príplatok 20€/rok)",
        "Max 20 PHP Workerov",
        "Memory limit a PHP limit: zvýšený",
        "Dáta na NVMe diskoch",
        "Zdarma SSL certifikát LETSENCRYPT",
        "Cloudflare ochrana + správa DNS/Cloudflare",
        "Správa Mail adries s DKIM a SPF",
        "Antispam ochrana mailov",
        "Neobmedzený počet emailových schránok",
        "Záloha DB/WEB/MAIL 1x denne po dobu 15 dní",
        "Podpora mailom a telefonicky",
        "Asistencia pri migrácii webu 1x ročne (do 1h)",
      ],
      ideal: "Firemné webstránky a e-shopy",
    },
    {
      id: "maximum",
      name: "MAXIMUM",
      price: { yearly: 199 },
      popular: false,
      features: [
        "Max 4-10 domén (viac po dohode)",
        "Max 100GB priestor (viac po dohode)",
        "Až 35 PHP Workerov",
        "Zvýšený memory limit a upload limit",
        "Dáta na NVMe diskoch",
        "Zvýhodnená cena COMODO certifikátu (až -30%)",
        "Správa certifikátov v cene",
        "Rozšírená podpora PHP verzií",
        "Testovací priestor pre nové verzie",
        "Vyšší výkon hostingu",
        "Záloha DB 2x denne, WEB/MAIL 1x denne po 30 dní",
        "Podpora telefón/email/AnyDesk, zrýchlená odozva",
        "Asistencia pri migrácii webu 1x ročne (do 2h)",
      ],
      ideal: "Veľké projekty a náročné aplikácie",
    },
  ];

  const domainPrices = [
    { extension: ".sk", price: "15,90€", period: "rok" },
    { extension: ".cz", price: "9,90€", period: "rok" },
    { extension: ".eu", price: "6,90€", period: "rok" },
    { extension: ".com", price: "15,90€", period: "rok" },
    { extension: ".hu", price: "15,90€", period: "2 roky" },
    { extension: ".pl", price: "19,90€", period: "2 roky" },
    { extension: ".uk", price: "16,90€", period: "2 roky" },
  ];

  const features = [
    {
      icon: FaRocket,
      title: "Vysoký výkon",
      description:
        "SSD disky a optimalizovaný server pre maximálnu rýchlosť vašich webstránok.",
    },
    {
      icon: FaShieldAlt,
      title: "Bezpečnosť",
      description:
        "SSL certifikáty, firewall, malware skenovanie a pravidelné bezpečnostné aktualizácie.",
    },
    {
      icon: FaHeadset,
      title: "24/7 Podpora",
      description:
        "Náš technický tím je k dispozícii kedykoľvek potrebujete pomoc alebo radu.",
    },
    {
      icon: FaDatabase,
      title: "Zálohovanie",
      description:
        "Automatické denné zálohy vašich dát s možnosťou okamžitého obnovenia.",
    },
    {
      icon: FaGlobe,
      title: "Globálna sieť",
      description: "CDN sieť pre rýchle načítanie webstránok po celom svete.",
    },
    {
      icon: FaCog,
      title: "Jednoduché správa",
      description:
        "Intuitívny cPanel pre ľahkú správu webstránok, emailov a databáz.",
    },
  ];

  const services = [
    {
      icon: FaGlobe,
      title: "Registrácia domén",
      description:
        "Registrujte si doménu s koncovkami .sk, .com, .eu a ďalšími.",
      link: "/webhosting/domeny",
      features: [
        "Viac ako 500 koncoviek",
        "DNS správa",
        "WHOIS ochrana",
        "Automatické obnovenie",
      ],
    },
    {
      icon: FaEnvelope,
      title: "Profesionálny email",
      description:
        "Vytvorte si profesionálne emailové adresy s vaším doménovým menom.",
      link: "/webhosting/webmail",
      features: [
        "Neobmedzené emailové schránky",
        "Webmail prístup",
        "IMAP/POP3 podpora",
        "Anti-spam ochrana",
      ],
    },
    {
      icon: FaServer,
      title: "Dedikovaný hosting",
      description:
        "Výkonné servery pre náročné aplikácie a vysokú návštevnosť.",
      link: "/webhosting/dedikovany",
      features: [
        "Plný root prístup",
        "Vlastné IP adresy",
        "Vysoký výkon",
        "99.9% uptime garancia",
      ],
    },
    {
      icon: FaCloud,
      title: "Cloud riešenia",
      description: "Škálovateľné cloud hosting riešenia pre moderné aplikácie.",
      link: "#",
      features: [
        "Auto-scaling",
        "Load balancing",
        "Mikroslužby podpora",
        "Kontajnerizácia",
      ],
    },
  ];

  const testimonials = [
    {
      name: "Peter Novák",
      company: "TechStart s.r.o.",
      rating: 5,
      text: "Výborná rýchlosť a spoľahlivosť. Naša webstránka sa načítava bleskovo a zákaznícka podpora je na najvyššej úrovni.",
    },
    {
      name: "Mária Svobodová",
      company: "E-shop Fashion",
      rating: 5,
      text: "Prechádzali sme z iného poskytovateľa a rozdiel je obrovský. Konečne máme hosting, na ktorý sa môžeme spoľahnúť.",
    },
    {
      name: "Tomáš Krejčí",
      company: "WebDesign Studio",
      rating: 5,
      text: "Pre naše klientské projekty používame INVER hosting už 3 roky. Technická podpora je vždy ochotná a kompetentná.",
    },
  ];

  const stats = [
    { number: "1 000+", label: "Spokojných klientov" },
    { number: "99.9%", label: "Uptime garancia" },
    { number: "24/7", label: "Technická podpora" },
    { number: `${yearsInBusiness}+`, label: "Rokov skúseností" },
  ];

  const calculatePrice = (plan) => {
    return billingCycle === "yearly" ? plan.price.yearly : plan.price.monthly;
  };

  const calculateSavings = (plan) => {
    const monthlyCost = plan.price.monthly * 12;
    const yearlyCost = plan.price.yearly * 12;
    const savings = monthlyCost - yearlyCost;
    return savings.toFixed(2);
  };

  return (
    <div className="webhosting-page">
      {/* Hero Section */}
      <section className="webhosting-hero">
        {/* <div className="container">
          <div className="hero-content">
            <p>
              Zjednocujeme všetky dielčie služby (správa domény, email hosting,
              webhosting) do jedného spojeného balíčka. Okrem platby za
              príslušný balíček klient platí ročne aj poplatok za samotnú
              doménu. V cene domény je automaticky aj služba "parkovania" domény
              pre registrované, ale nevyužívané domény.
            </p>
          </div>
        </div> */}
        <h1>
          <span className="hero-title-main">WEBHOSTING</span>
          <span className="hero-title-connector">&</span>
          <span className="hero-title-main">MAILHOSTING</span>
        </h1>
        <div className="hero-stats">
          {stats.map((stat, index) => (
            <div key={index} className="hero-stat">
              <span className="stat-number">{stat.number}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
        <p className="pricing-note">Cenník služieb platný od 1.1.2025</p>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="container">
          <div className="section-header">
            <h2>Prečo si vybrať náš hosting?</h2>
            <p>
              Poskytujeme kompletné hosting riešenia s dôrazom na výkon,
              bezpečnosť a spoľahlivosť
            </p>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">
                  <feature.icon />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="container">
          <div className="section-header">
            <h2>Vyberte si hosting balík</h2>
            <p>
              Tri úrovne balíčkov podľa objemu dát a náročnosti vašej web
              stránky
            </p>
          </div>

          <div className="pricing-grid">
            {hostingPlans.map((plan) => (
              <div
                key={plan.id}
                className={`pricing-card ${plan.popular ? "popular" : ""} ${
                  selectedPlan === plan.id ? "selected" : ""
                }`}
                onClick={() => setSelectedPlan(plan.id)}
              >
                {plan.popular && (
                  <div className="popular-badge">Najpopulárnejší</div>
                )}

                <div className="plan-header">
                  <h3>{plan.name}</h3>
                  <div className="price">
                    <span className="currency">€</span>
                    <span className="amount">{plan.price.yearly}</span>
                    <span className="period">/rok + DPH</span>
                  </div>
                  <p className="plan-description">{plan.ideal}</p>
                </div>

                <div className="plan-features">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="feature-item">
                      <FaCheck />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <button className="btn btn-primary btn-full">
                  Objednať teraz
                  <FaArrowRight />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Domain Prices Section */}
      <section className="domain-prices-section">
        <div className="container">
          <div className="section-header">
            <h2>Ceny domén</h2>
            <p>Registrujeme takmer všetky svetové domény</p>
          </div>

          <div className="domains-grid">
            {domainPrices.map((domain, index) => (
              <div key={index} className="domain-card">
                <div className="domain-extension">{domain.extension}</div>
                <div className="domain-price">{domain.price}</div>
                <div className="domain-period">/{domain.period}</div>
              </div>
            ))}
          </div>

          <div className="domain-note">
            <p>
              <strong>... a množstvo ďalšich</strong>
            </p>
            <p>
              Sme schopní registrovať aj iné zahraničné domény. Registrujeme
              takmer všetky svetové domény, okolité krajiny, Európa, Ázia,
              Amerika...
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      {/* <section className="services-section">
        <div className="container">
          <div className="section-header">
            <h2>Doplnkové služby</h2>
            <p>Kompletné riešenia pre vašu online prezentáciu</p>
          </div>

          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card">
                <div className="service-icon">
                  <service.icon />
                </div>
                <div className="service-content">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <ul className="service-features">
                    {service.features.map((feature, idx) => (
                      <li key={idx}>
                        <FaCheck />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to={service.link} className="service-link">
                    Zistiť viac
                    <FaArrowRight />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Performance Section */}
      <section className="performance-section">
        <div className="container">
          <div className="performance-content">
            <div className="performance-text">
              <h2>Výkon na najvyššej úrovni</h2>
              <p>
                Naše servery sú optimalizované pre maximálny výkon a
                spoľahlivosť. Používame najnovšie technológie a hardware pre
                zabezpečenie rýchlosti a stability vašich webstránok.
              </p>

              <div className="performance-features">
                <div className="perf-feature">
                  <FaTachometerAlt />
                  <div>
                    <h4>SSD úložisko</h4>
                    <p>Až 10x rýchlejšie než tradičné HDD disky</p>
                  </div>
                </div>
                <div className="perf-feature">
                  <FaCloud />
                  <div>
                    <h4>CDN sieť</h4>
                    <p>Globálna distribúcia obsahu pre rýchle načítanie</p>
                  </div>
                </div>
                <div className="perf-feature">
                  <FaShieldAlt />
                  <div>
                    <h4>DDoS ochrana</h4>
                    <p>Pokročilá ochrana proti kybernetickým útokom</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="performance-metrics">
              <div className="metric">
                <div className="metric-icon">
                  <FaDownload />
                </div>
                <div className="metric-value">1.2s</div>
                <div className="metric-label">Priemerný čas načítania</div>
              </div>
              <div className="metric">
                <div className="metric-icon">
                  <FaUpload />
                </div>
                <div className="metric-value">99.9%</div>
                <div className="metric-label">Uptime garancia</div>
              </div>
              <div className="metric">
                <div className="metric-icon">
                  <FaUsers />
                </div>
                <div className="metric-value">24/7</div>
                <div className="metric-label">Monitorovanie</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {/* <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <h2>Čo hovoria naši klienti</h2>
            <p>Spokojnosť klientov je naša priorita</p>
          </div>

          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} />
                  ))}
                </div>
                <p>"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <strong>{testimonial.name}</strong>
                  <span>{testimonial.company}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <div className="pricing-disclaimer">
              <p>
                <strong>Poznámka:</strong> Všetky uvedené ceny sú uvádzané bez
                dane z pridanej hodnoty, ktorá je pripočítaná v aktuálne platnej
                sadzbe.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WebhostingPage;
